import React, { useState, useEffect, useCallback } from 'react';
import PlayerForm from './PlayerForm';
import { PlayerUpload } from './PlayerUpload';
import AvailablePlayers from './AvailablePlayers';
import { Teams } from './Teams';
import { Player, SelectionData, SkillDistribution, CsvUploadResult } from '../Model';
import { restClient } from '../RestClient';
import { PageLoading } from '../PageLoading';
import { useReferenceData } from '../ReferenceDataContext';

interface TeamSelectionProps {
  onError: (message: string, type?: 'error' | 'warning' | 'info') => void;
}

export function TeamSelection({ onError }: TeamSelectionProps) {
  const { selectedSelectionId, referenceData } = useReferenceData();

  const selectedSelection = referenceData?.selections.find(s => s.id === selectedSelectionId);
  const canEdit = selectedSelection?.canEdit ?? false;
  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState<number>(9);
  const [skillDistribution, setSkillDistribution] = useState<SkillDistribution>({
    developing: 15,
    average: 45,
    good: 85
  });
  const [inputMode, setInputMode] = useState<'manual' | 'csv'>('manual');
  const [showArchived, setShowArchived] = useState(false);

  const fetchSelectionData = useCallback(async () => {
    if (selectedSelectionId === null) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await restClient.getSelectionData(selectedSelectionId);
      if (result.IsSuccess) {
        setSelectionData(result.Value);
        setMaxPlayers(result.Value.maxPlayers);
        setSkillDistribution(result.Value.skillDistribution);
        console.log(`Selection data loaded with ${result.Value.players?.length || 0} players for selection ID: ${selectedSelectionId}`);
      } else {
        console.error('Error fetching selection data:', result.Error);
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error fetching selection data:', error);
      onError('Unexpected error occurred while loading selection data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSelectionId, onError]);

  useEffect(() => {
    fetchSelectionData();
  }, [fetchSelectionData]);

  const addPlayer = useCallback(async (player: Player) => {
    try {
      if (!selectionData) {
        onError('No selection data available');
        return;
      }

      const result = await restClient.addPlayer(player, selectionData.id);
      if (result.IsSuccess) {
        const newPlayer = result.Value;
        setSelectionData(prev => prev ? {
          ...prev,
          players: [...prev.players, newPlayer]
        } : null);
      } else {
        console.error('Error adding player:', result.Error);
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error adding player:', error);
      onError('Unexpected error occurred while adding player');
    }
  }, [selectionData, onError]);

  const updatePlayer = useCallback(async (player: Player) => {
    try {
      const result = await restClient.updatePlayer(player);
      if (result.IsSuccess) {
        const updatedPlayer = result.Value;
        setSelectionData(prev => prev ? {
          ...prev,
          players: prev.players.map(p => p.id === player.id ? updatedPlayer : p)
        } : null);
        setSelectedPlayer(null);
      } else {
        console.error('Error updating player:', result.Error);
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error updating player:', error);
      onError('Unexpected error occurred while updating player');
    }
  }, [onError]);

  const handlePlayerSelect = useCallback((player: Player) => {
    setSelectedPlayer(player);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedPlayer(null);
  }, []);

  const handleAddPlayerToTeam = useCallback((playerId: number, teamId: number) => {
    console.log(`Adding player ${playerId} to team ${teamId}`);
    if (!selectionData) return;

    const playerToAdd = selectionData.players.find(p => p.id === playerId);
    if (!playerToAdd) {
      console.log(`Player ${playerId} not found`);
      return;
    }

    console.log(`Found player:`, playerToAdd);
    console.log(`Current teams:`, selectionData.teams);

    setSelectionData(prev => {
      if (!prev) return null;
      const updatedTeams = prev.teams.map(team => {
        if (team.id === teamId) {
          const updatedTeam = {
            ...team,
            players: [...(team.players || []), playerToAdd]
          };
          console.log(`Updated team ${teamId}:`, updatedTeam);
          return updatedTeam;
        }
        return team;
      });
      return { ...prev, teams: updatedTeams };
    });
  }, [selectionData]);

  const handleMaxPlayersChange = useCallback((newMaxPlayers: number) => {
    setMaxPlayers(newMaxPlayers);
  }, []);

  const handleSkillDistributionChange = useCallback((distribution: SkillDistribution) => {
    setSkillDistribution(distribution);
  }, []);

  const handleCsvUploadComplete = useCallback((result: CsvUploadResult) => {
    if (selectionData && result.players.length > 0) {
      setSelectionData(prev => {
        if (!prev) return null;

        const existingPlayersMap = new Map(prev.players.map(p => [p.id, p]));

        result.players.forEach(player => {
          existingPlayersMap.set(player.id, player);
        });

        return {
          ...prev,
          players: Array.from(existingPlayersMap.values())
        };
      });
    }
  }, [selectionData]);

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <>
      {canEdit && (
        <>
          <div className="input-mode-toggle">
            <div className="segmented-control">
              <button
                onClick={() => setInputMode('manual')}
                className={`segmented-control-btn ${inputMode === 'manual' ? 'segmented-control-btn-active' : ''}`}
              >
                Add Manually
              </button>
              <button
                onClick={() => setInputMode('csv')}
                className={`segmented-control-btn ${inputMode === 'csv' ? 'segmented-control-btn-active' : ''}`}
              >
                Upload CSV
              </button>
            </div>
          </div>

          {inputMode === 'manual' ? (
            <PlayerForm
              onAddPlayer={addPlayer}
              onUpdatePlayer={updatePlayer}
              selectedPlayer={selectedPlayer}
              onClearSelection={handleClearSelection}
              skillNames={selectionData?.skillNames || []}
              selectionDataId={selectionData?.id || 0}
            />
          ) : (
            <PlayerUpload
              selectionDataId={selectionData?.id || 0}
              onUploadComplete={handleCsvUploadComplete}
              onError={onError}
            />
          )}
        </>
      )}

      <div className="team-selection-layout">
        <div className="team-selection-sidebar">
          <AvailablePlayers
            selectionData={selectionData}
            onPlayerSelect={canEdit ? handlePlayerSelect : undefined}
            selectedPlayer={selectedPlayer}
            maxPlayers={maxPlayers}
            onAddPlayerToTeam={handleAddPlayerToTeam}
            skillDistribution={skillDistribution}
            showArchived={showArchived}
            onShowArchivedChange={setShowArchived}
          />
        </div>
        <div className="team-selection-content">
          <Teams
            selectionData={selectionData}
            onError={onError}
            onSelectionDataChange={setSelectionData}
            onMaxPlayersChange={handleMaxPlayersChange}
            onSkillDistributionChange={handleSkillDistributionChange}
          />
        </div>
      </div>
    </>
  );
}
